import React, { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useDebounce } from '@/hooks/useDebounce';
import { format } from 'date-fns';

import SvgCalendarDay from '@/components/common/Icons/CalendarDay';
import { LexicalViewer } from '@/components/common/LexicalEditorComponent/LexicalViewer';
import { Pagination } from '@/components/common/Pagination';
import {
  OrderTypes,
  PageBlockStyles,
  PageSocialNetworksBlock,
  useCarouselEventsQuery,
  useSocialNetworksEventsQuery,
  VersionStates,
} from '@/components/common/Providers/GraphqlProvider/generated';
import { SearchInput } from '@/components/ui/SearchInput';

// import SearchInput from '@/components/common/SearchInput'

import NewsSocialMedia from '../../blocks/PageNewsletterBlock/public2/components/NewsSocialMedia';

const MarkdownPreview = dynamic(() => import('@uiw/react-markdown-preview'), {
  ssr: false,
});

export const EventsSocialNetworks = () => {
  const { push } = useRouter();
  const [page, setPage] = useState(1);
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(10);
  const [filter, setFilter] = useState('');

  const debounceSeatchText = useDebounce(filter, 700);
  const queryVariables = useMemo(
    () => ({
      orderBy: {
        startDate: OrderTypes.Desc,
      },
      pagination: {
        skip: skip,
        take: take,
      },
      where: {
        // eventStatus: {
        //   _eq: VersionStates.Active,
        // },
        ...(debounceSeatchText.trim().length > 0
          ? {
              eventItemTitle: {
                _contains: debounceSeatchText.trim(),
              },
            }
          : {}),
      },
    }),
    [skip, take, debounceSeatchText]
  );

  const { data: eventsData } = useCarouselEventsQuery({
    variables: queryVariables,
    fetchPolicy: 'cache-first',
  });

  const { data: socialNetworksData } = useSocialNetworksEventsQuery();

  const socialNetworks: PageSocialNetworksBlock | undefined = useMemo(() => {
    const pageBlock = socialNetworksData?.mainPage.find(
      (x) => x.__typename == 'PageSocialNetworksBlock'
    ) as PageSocialNetworksBlock;
    if (pageBlock) {
      return pageBlock;
    }
    return undefined;
  }, [socialNetworksData]);

  useEffect(() => {
    setSkip(0);
  }, [debounceSeatchText]);

  const blockStyle = {
    isA:
      !socialNetworks ||
      !socialNetworks.pageBlockStyle ||
      socialNetworks.pageBlockStyle === PageBlockStyles.A,
    isB: socialNetworks?.pageBlockStyle === PageBlockStyles.B,
    isC: socialNetworks?.pageBlockStyle === PageBlockStyles.C,
  };

  const onSelectEvent = (id: string) => {
    push(`/events/${id}`);
  };

  return (
    <div className="mt-8">
      <div className="inner-extend">
        <hr className="border-1 mb-7  mt-10 border-gray-400"></hr>

        <p className="font-montserrat text-[24px] font-semibold text-primary">
          Lista de eventos y redes sociales
        </p>
        <div className="mb-3 mt-5 flex gap-6">
          <div className="flex flex-1 flex-col">
            <div className={'mb-4 w-full'}>
              <SearchInput
                placeholder={'Buscar evento...'}
                className={'w-full'}
                color="primary"
                value={filter}
                onClear={() => {
                  setFilter('');
                }}
                onChange={(value) => {
                  setFilter(value.target.value);
                }}
              />
            </div>

            {eventsData?.eventsItems?.map((event, index) => (
              <CardEvent
                key={index}
                event={event}
                index={index}
                onClick={() => onSelectEvent(event.id)}
              />
            ))}
          </div>

          <div className="flex flex-1 flex-col gap-2">
            {socialNetworks &&
              socialNetworks.rightItems?.length > 0 &&
              socialNetworks.pageBlockStyle && (
                <>
                  <p className="text-[20px] font-semibold leading-[30px] text-primary">
                    Nuestras redes
                  </p>
                  <NewsSocialMedia
                    items={socialNetworks.rightItems.map((e) => ({
                      id: e.id,
                      link: e.linkItem?.url,
                      snsSocialNetworkType: e.socialNetworkType,
                      orderIndex: e.orderIndex,
                    }))}
                    blockStyle={blockStyle}
                  />
                </>
              )}
          </div>
        </div>
        <Pagination
          currentPage={page}
          totalPages={eventsData?.eventsItemsCount?.totalPages || 1}
          handlePageChange={(page) => {
            setPage(page);
            setSkip((page - 1) * take);
          }}
        />
      </div>
    </div>
  );
};

export const CardEvent = ({
  event,
  index = 0,
  onClick,
}: {
  event: any;
  index?: number;
  onClick?: () => void;
}) => {
  const { locale } = useRouter();
  const of = locale === 'es' ? "'de'" : '';

  return (
    <div className="flex flex-col">
      {index != 0 && <div className="my-2 w-full border-t border-t-[#BABABA]"></div>}

      <div
        className="group flex cursor-pointer flex-col justify-center gap-6 rounded-[10px] border border-[#F6F8F9] px-[10px] py-6 transition-all hover:border-primary"
        onClick={onClick ? onClick : () => {}}
      >
        <p className="line-clamp-2 font-montserrat text-[18px] font-semibold text-black transition-all group-hover:text-gold group-hover:underline">
          <LexicalViewer value={event.eventItemTitle} />
        </p>
        <div
          className={
            'flex w-[227px] items-center gap-[12px] rounded-[20px] bg-primaryLight p-[8px_12px]'
          }
        >
          <SvgCalendarDay color="primaryDark" className="fill-primary" />
          <p className="font-workSans text-[14px] font-medium text-primaryDark">
            {format(new Date(event.startDate), `dd ${of} MMMM ${of} yyyy`)}
          </p>
        </div>
        <p className="line-clamp-3 font-workSans text-[14px] font-normal leading-[16.8px] text-tundora">
          {/* <LexicalViewer value={event.eventItemDescription} /> */}
          <MarkdownPreview source={event.eventItemDescription} />
        </p>
      </div>
    </div>
  );
};
